import React, { useEffect, useState } from 'react'
import { getLocalizedValue } from './PageDetail'
import { useSEO } from '../lib/useSEO'

export default function Kontakt() {
  const [page, setPage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error'
  const [errorMessage, setError] = useState('')

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/kontakt')
      .then(res => res.json())
      .then(data => {
        setPage(data)
        setLoading(false)
      })
      .catch(err => {
        console.error(err)
        setLoading(false)
      })
  }, [])

  useSEO({
    title: page ? getLocalizedValue(page.title) : 'Kontakt · Vikar4',
    description: page ? (getLocalizedValue(page.meta_description) || getLocalizedValue(page.og_description) || 'Kontakt Vikar4 for fleksible vikarløsninger eller hvis du søger nyt job. Vi sidder klar ved telefonen og tasterne.') : 'Kontakt Vikar4 for fleksible vikarløsninger eller hvis du søger nyt job. Vi sidder klar ved telefonen og tasterne.',
    ogTitle: page ? getLocalizedValue(page.og_title) : 'Kontakt · Vikar4',
    ogDescription: page ? (getLocalizedValue(page.og_description) || getLocalizedValue(page.meta_description)) : '',
    canonicalPath: '/kontakt'
  })

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitStatus(null)
    setError('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          email: '',
          message: '',
          website_url: ''
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        setSubmitStatus('error')
        setError(errData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.')
      }
    } catch (err) {
      console.error(err)
      setSubmitStatus('error')
      setError('Netværksfejl. Kontroller din internetforbindelse og prøv igen.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Page Header - Hero Banner Component */}
      <section className="relative h-[240px] md:h-[320px] bg-[#015D9A] flex items-center justify-center overflow-hidden">
        {/* Background Image with Cool-toned Dark Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center mix-blend-multiply opacity-60 bg-[url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/7b6c8b8c-dd85-4faa-a1de-b9b48f1b2725/industrial-subpage-hero.jpg')]"
        />
        {/* Centered Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-wide">
            {getLocalizedValue(page?.title) || 'Kontakt'}
          </h1>
        </div>
      </section>

      {/* Main Content - Two-Column Main Layout Content */}
      <main className="max-w-5xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid grid-cols-1 gap-12 md:items-start">
          
          {/* Content Column (Contact Details + Form) */}
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:items-start">
            {/* Contact Details */}
            <div className="md:col-span-6 flex flex-col gap-8">
              <div>
                <h2 className="text-3xl font-extrabold text-[#0f3d5c] tracking-tight">
                  Kontakt os i dag
                </h2>
                <div className="mt-6 flex flex-col gap-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                  <h3 className="text-xl font-bold text-[#0f3d5c] mt-2">Et vikarbureau med lokale rødder</h3>
                  <p>
                    Vikar4 har base i en lille by mellem Aalborg og Hadsund ved navn Bælum. Herfra tilbyder vi vikarservice i et større geografisk område – fra Midtjylland til spidsen og Nordjylland. I modsætning til de store vikarbureauer, har vi ingen ambitioner om at dække hele landet. Gennem tæt samarbejde og dialog, ved vi hvad der sker i det lokale erhvervsliv og dermed hvilke virksomheder, der har behov for vikarer.
                  </p>
                  <p>
                    Hvis du vil være vikar, så send os en ganske kort beskrivelse af dine kvalifikationer plus væsentligste tidligere erhvervserfaringer. Et CV er meget fint.
                  </p>
                  <p>
                    Sidder du som arbejdsgiver, produktionsansvarlig eller lignende med problemer i forhold til eksempelvis rekruttering af personale i spidsbelastningsperioder eller ferier kan vi helt sikkert hjælpe. Ring eller skriv, vi kommer gerne og får en snak om, hvorvidt vi kan hjælpe dig.
                  </p>
                </div>
              </div>

              <div className="rounded-sm border border-slate-100 bg-slate-50 p-8 flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-100 text-[#015D9A]">
                      <i className="fa-solid fa-phone" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium">Tlf</p>
                      <a href="tel:27606027" className="text-sm font-bold text-[#015D9A] hover:underline">
                        (+45)27 60 60 27
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-100 text-[#015D9A]">
                      <i className="fa-solid fa-envelope" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium">Mail</p>
                      <a href="mailto:job@vikar4.dk" className="text-sm font-bold text-[#015D9A] hover:underline">
                        job@vikar4.dk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-100 text-[#015D9A]">
                      <i className="fa-solid fa-building" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium">CVR</p>
                      <p className="text-sm font-semibold text-slate-800">40487360</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-100 text-[#015D9A]">
                      <i className="fa-solid fa-location-dot" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium">Adresse</p>
                      <div className="text-sm font-semibold text-slate-800 flex flex-col">
                        <span>Skovhusevej 22, 9574 Bælum</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-6">
              <div className="rounded-sm border border-slate-100 bg-white p-8 shadow-none">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-[#0f3d5c] mb-2">Bliv vikar hos os</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Ønsker du at være vikar hos os, så skriv kort om dig selv.
                  </p>
                </div>

                <hr className="border-slate-100 my-6" />

                <h3 className="text-xl font-bold text-[#0f3d5c] mb-6">Skriv til os</h3>

                {submitStatus === 'success' && (
                  <div className="mb-6 rounded-sm bg-emerald-50 p-4 text-emerald-800 border border-emerald-100 flex items-start gap-3">
                    <i className="fa-solid fa-circle-check mt-0.5 text-lg text-emerald-600" />
                    <div>
                      <h3 className="font-bold">Beskeden blev sendt!</h3>
                      <p className="text-sm mt-1">Tak for din henvendelse. Vi vender tilbage til dig hurtigst muligt.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 rounded-sm bg-rose-50 p-4 text-rose-800 border border-rose-100 flex items-start gap-3">
                    <i className="fa-solid fa-circle-exclamation mt-0.5 text-lg text-rose-600" />
                    <div>
                      <h3 className="font-bold">Der opstod en fejl</h3>
                      <p className="text-sm mt-1">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                    <label htmlFor="website_url">Please do not fill in this field.</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Navn: <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full rounded-sm border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-[#015D9A] focus:outline-none focus:ring-1 focus:ring-[#015D9A] transition-all"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Email: <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-sm border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-[#015D9A] focus:outline-none focus:ring-1 focus:ring-[#015D9A] transition-all"
                      placeholder="din.email@adresse.dk"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Besked: <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full rounded-sm border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-[#015D9A] focus:outline-none focus:ring-1 focus:ring-[#015D9A] transition-all resize-none"
                      placeholder="Skriv din besked her..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-2 w-full rounded-sm bg-[#015D9A] py-3.5 text-center text-sm font-semibold text-white hover:bg-[#014d80] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {submitting ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Sender besked...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        Send besked
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
